/**
 * Product / marketing analytics: PostHog + first-party Testera pageview beacon.
 */

import posthog from 'posthog-js';
import { getHostname, isAdminHost } from '../utils/appOrigin';

let initialized = false;
let lastPath = '';

function apiBase() {
  const injected = process.env.REACT_APP_API_BASE_URL;
  if (injected) return String(injected).replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`;
  }
  return '/api';
}

function surfaceForHost(host) {
  const h = String(host || '').toLowerCase();
  if (h === 'admin.testera.io' || h.startsWith('admin.')) return 'admin';
  if (h === 'app.testera.io' || h.startsWith('app.') || (h.endsWith('.testera.io') && h !== 'testera.io' && h !== 'www.testera.io' && h !== 'mcp.testera.io')) {
    return 'app';
  }
  if (h === 'testera.io' || h === 'www.testera.io') return 'marketing';
  return 'other';
}

function exceptionMessages(event) {
  const list = event?.properties?.$exception_list;
  if (Array.isArray(list) && list.length) {
    return list.map((ex) => String(ex?.value || ex?.$exception_message || '').trim());
  }
  const single = String(
    event?.properties?.$exception_message || event?.properties?.$exception_value || ''
  ).trim();
  return single ? [single] : [];
}

function exceptionTypes(event) {
  const list = event?.properties?.$exception_list;
  if (Array.isArray(list) && list.length) {
    return list.map((ex) => String(ex?.type || '').trim());
  }
  const single = String(event?.properties?.$exception_type || '').trim();
  return single ? [single] : [];
}

/**
 * Browsers scrub cross-origin errors (no CORS / missing crossorigin) to opaque
 * "Script error." with no stack — unactionable noise, often from Ads/gtag injects.
 */
function isOpaqueScriptError(event) {
  if (!event || event.event !== '$exception') return false;
  const list = event.properties?.$exception_list;
  const values = Array.isArray(list) ? list : [];
  const msgs = exceptionMessages(event);
  if (values.length === 0) {
    return msgs.every((msg) => /^script error\.?$/i.test(msg));
  }
  return values.every((ex) => {
    const msg = String(ex?.value || ex?.$exception_message || '').trim();
    if (!/^script error\.?$/i.test(msg)) return false;
    const frames = ex?.stacktrace?.frames || ex?.stack_trace?.frames || [];
    return !Array.isArray(frames) || frames.length === 0;
  });
}

/** Android in-app browsers / WebViews — not our code. */
function isAndroidBridgeNoise(event) {
  if (!event || event.event !== '$exception') return false;
  return exceptionMessages(event).some((msg) =>
    /Java object is gone|Error invoking postMessage/i.test(msg)
  );
}

const CHUNK_ERROR_RE = /Loading chunk \S+ failed|ChunkLoadError/i;

/** Lazy chunk failed to load (stale tab after a deploy, flaky network, sandboxed scanner). */
export function isChunkLoadMessage(message) {
  return CHUNK_ERROR_RE.test(String(message || ''));
}

function isChunkLoadError(event) {
  if (!event || event.event !== '$exception') return false;
  const types = exceptionTypes(event);
  const msgs = exceptionMessages(event);
  return types.some((t) => /ChunkLoadError/i.test(t)) || msgs.some(isChunkLoadMessage);
}

/**
 * Microsoft SafeLinks / Defender link scanners pre-render pages in a sandbox and
 * inject a script that rejects with "Object Not Found Matching Id:N, MethodName:update,
 * ParamCount:4". Those sessions are automated, not users — drop them.
 */
function isLinkScannerNoise(event) {
  if (!event || event.event !== '$exception') return false;
  return exceptionMessages(event).some((msg) =>
    /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/i.test(msg)
  );
}

/**
 * Turn a chunk-load exception into a small countable event instead of an
 * error-tracking issue: the failure is environmental (deploy skew, network,
 * sandboxed scanners) but we still want to see how often it happens, on which
 * surface, and whether the automatic recovery kicked in.
 */
function toChunkLoadFailedEvent(event) {
  const msg = exceptionMessages(event).find(isChunkLoadMessage) || exceptionMessages(event)[0] || '';
  const reason = (msg.match(/\((timeout|missing|error|load)[:)]/i) || [])[1] || 'unknown';
  const url = (msg.match(/https?:\/\/\S+?\.js/i) || [])[0] || '';
  const chunk = (msg.match(/Loading chunk (\S+) failed/i) || [])[1] || '';
  return {
    ...event,
    event: 'chunk_load_failed',
    properties: {
      testera_host: event.properties?.testera_host,
      testera_surface: event.properties?.testera_surface,
      $current_url: event.properties?.$current_url,
      $session_id: event.properties?.$session_id,
      $window_id: event.properties?.$window_id,
      $lib: event.properties?.$lib,
      $lib_version: event.properties?.$lib_version,
      $browser: event.properties?.$browser,
      $browser_version: event.properties?.$browser_version,
      $os: event.properties?.$os,
      chunk,
      chunk_url: url,
      reason: reason.toLowerCase(),
      recovery_attempted: chunkRecoveryAttempted(),
      source: event.properties?.source || 'window',
    },
  };
}

export function beforeSend(event) {
  if (!event) return event;
  if (isOpaqueScriptError(event)) return null;
  if (isAndroidBridgeNoise(event)) return null;
  if (isLinkScannerNoise(event)) return null;
  if (isChunkLoadError(event)) return toChunkLoadFailedEvent(event);
  return event;
}

const CHUNK_RELOAD_KEY = 'testara_chunk_reload';

function chunkRecoveryAttempted() {
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * One soft reload per tab when a lazy chunk fails (stale HTML/JS after a
 * deploy). Returns true when a reload was triggered, false when this tab
 * already tried once (the caller should then show its error UI) or the
 * message is not a chunk failure. Shared by the window listeners and by
 * ErrorBoundary, because React.lazy failures surface through Suspense and
 * never reach the window 'error' / 'unhandledrejection' handlers.
 */
let chunkReloadInFlight = false;

export function attemptChunkLoadRecovery(message) {
  if (typeof window === 'undefined') return false;
  if (!isChunkLoadMessage(message)) return false;
  // React (dev replay, several lazy boundaries) can ask more than once while
  // the reload we already triggered is still in flight: keep answering yes.
  if (chunkReloadInFlight) return true;
  if (chunkRecoveryAttempted()) return false;
  chunkReloadInFlight = true;
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  } catch { /* private mode: still reload once per page life */ }
  window.location.reload();
  return true;
}

/** Test hook: a fresh page load starts with no reload in flight. */
export function resetChunkLoadRecoveryForTests() {
  chunkReloadInFlight = false;
  clearChunkLoadRecoveryMarker();
}

/** Forget the one-shot marker once a page has loaded cleanly, so a later deploy can recover again. */
export function clearChunkLoadRecoveryMarker() {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch { /* ignore */ }
}

/**
 * Drop service-worker caches and unregister workers, then reload. The plain
 * "clear cache" path only evicted localStorage, which does nothing for a
 * worker still serving a stale app shell.
 */
export async function hardResetClientCaches() {
  if (typeof window === 'undefined') return;
  try {
    if (window.caches?.keys) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
  } catch { /* ignore */ }
  try {
    const regs = await navigator.serviceWorker?.getRegistrations?.();
    await Promise.all((regs || []).map((r) => r.unregister()));
  } catch { /* ignore */ }
  clearChunkLoadRecoveryMarker();
}

function installChunkLoadRecovery() {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e) => attemptChunkLoadRecovery(e?.message));
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason;
    attemptChunkLoadRecovery(reason?.message || reason);
  });
  // A page that reached 'load' has its chunks: allow one more recovery later.
  window.addEventListener('load', () => {
    setTimeout(clearChunkLoadRecoveryMarker, 15_000);
  });
}

export function initProductAnalytics() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  installChunkLoadRecovery();

  const key = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST;
  if (process.env.NODE_ENV !== 'production') {
    if (!key) console.error('REACT_APP_POSTHOG_KEY variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once REACT_APP_POSTHOG_KEY is configured');
    if (!host) console.error('REACT_APP_POSTHOG_HOST variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once REACT_APP_POSTHOG_HOST is configured');
  }
  if (key && host && !isAdminHost()) {
    try {
      // Clear sticky PostHog Toolbar state so the floating debugger UI doesn't
      // reappear on the marketing/app sites (analytics capture is unaffected).
      try {
        window.localStorage?.removeItem('_postHogToolbarParams');
        if (String(window.location.hash || '').includes('__posthog')) {
          window.history.replaceState(
            null,
            '',
            `${window.location.pathname}${window.location.search || ''}`
          );
        }
      } catch { /* ignore */ }

      posthog.init(key, {
        api_host: host,
        person_profiles: 'identified_only',
        capture_pageview: false, // we capture on route change
        capture_pageleave: true,
        opt_in_site_apps: false,
        tracing_headers: [new URL(apiBase(), window.location.origin).hostname],
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
        before_send: beforeSend,
        persistence: 'localStorage+cookie',
        loaded: (ph) => {
          ph.register({
            testera_host: getHostname(),
            testera_surface: surfaceForHost(getHostname()),
          });
          // Never auto-open the PostHog Toolbar on Testera surfaces.
          try {
            if (ph.toolbar) {
              ph.toolbar.maybeLoadToolbar = () => false;
              ph.toolbar.loadToolbar = () => false;
            }
            if (typeof ph.loadToolbar === 'function') {
              ph.loadToolbar = () => false;
            }
          } catch { /* ignore */ }
        },
      });
    } catch (e) {
      console.warn('[analytics] PostHog init failed', e);
    }
  }

  // Initial pageview
  trackPageview();
}

export function trackPageview(pathname) {
  if (typeof window === 'undefined') return;
  const path = pathname || `${window.location.pathname}${window.location.search || ''}`;
  if (path === lastPath) return;
  lastPath = path;

  const host = getHostname();
  const surface = surfaceForHost(host);
  const payload = {
    path,
    host,
    referrer: document.referrer || '',
    title: document.title || '',
    surface,
    screenW: window.screen?.width || null,
    screenH: window.screen?.height || null,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
  };

  // First-party beacon (powers Admin → Visitors)
  try {
    const url = `${apiBase()}/analytics/pageview`;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch { /* ignore */ }

  // PostHog (skip admin host — ops traffic)
  if (!isAdminHost() && process.env.REACT_APP_POSTHOG_KEY) {
    try {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        path,
        testera_surface: surface,
      });
    } catch { /* ignore */ }
  }
}

export function identifyUser(user) {
  const userId = user?._id || user?.id;
  if (!userId || !process.env.REACT_APP_POSTHOG_KEY || isAdminHost()) return;

  const properties = {
    ...(user.email ? { email: user.email } : {}),
    ...(user.name ? { name: user.name } : {}),
    ...(user.tenant ? { tenant: String(user.tenant) } : {}),
    ...(user.role ? { role: user.role } : {}),
    ...(user.subscriptionPlan ? { subscription_plan: user.subscriptionPlan } : {}),
  };

  try {
    posthog.identify(String(userId), properties);
  } catch { /* ignore */ }
}

export function resetUser() {
  if (!process.env.REACT_APP_POSTHOG_KEY || isAdminHost()) return;
  try {
    posthog.reset();
  } catch { /* ignore */ }
}

/** One-time keys so sign_up never double-fires on remount/retry/navigation. */
const firedSignUps = new Set();

function hasGtmContainer() {
  if (typeof window === 'undefined') return false;
  if (window.google_tag_manager) return true;
  try {
    return !!document.querySelector('script[src*="googletagmanager.com/gtm.js"]');
  } catch {
    return false;
  }
}

function hasGtag() {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/**
 * Fire GA4 `sign_up` after a genuine account-creation success (not form open / hash route).
 * Uses GTM dataLayer when a GTM container is present; otherwise gtag.js.
 * @returns {boolean} true if the event was sent
 */
export function trackSignUp({ method = 'email', userId, value, currency } = {}) {
  if (typeof window === 'undefined') return false;

  const key = String(userId || '').trim();
  if (!key) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[analytics] sign_up skipped — missing userId for dedupe key');
    }
    return false;
  }
  if (firedSignUps.has(key)) return false;
  firedSignUps.add(key);

  const params = { method: String(method || 'email') };
  if (value != null && Number.isFinite(Number(value))) params.value = Number(value);
  if (currency) params.currency = String(currency);

  const useGtm = hasGtmContainer();
  const useGtag = !useGtm && hasGtag();

  if (!useGtm && !useGtag) {
    console.warn(
      '[analytics] sign_up not sent — neither gtag.js nor GTM is loaded on this page'
    );
    return false;
  }

  try {
    if (useGtm) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'sign_up', ...params });
    } else {
      window.gtag('event', 'sign_up', params);
    }
  } catch (e) {
    console.warn('[analytics] sign_up failed', e);
    return false;
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log('[analytics] sign_up event fired', {
      via: useGtm ? 'GTM dataLayer' : 'gtag',
      ...params,
      userId: key,
    });
  }
  return true;
}

